import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import type { StandardCart } from '@/types/ShopFront/CheckoutStandards'
import { error } from '@/services/Log'
import { EXTEND_SKU } from '@/data/constants/EXTEND_SKU'
import { checkIsExtendItem } from '@/helpers/checkIsExtendItem'

const getExtendingSku = (sku: string) => (
  checkIsExtendItem({ sku })
    ? sku.split(EXTEND_SKU)[1]
    : ''
)

const sumQuantitiesWithSku = (target: string) => (
  items: readonly { sku: string, quantity: number }[],
) => (
  items.reduce((acc, { sku, quantity }) => (
    sku === target
      ? acc + quantity
      : acc
  ), 0)
)
type IDAndQuantitTuple = [string, number]

type CartOperatorResponse = Promise<{ success: boolean, newCart: StandardCart | null }>

const getLastPromiseToResolve = (promises: Awaited<CartOperatorResponse>[]) => (
  promises.sort(({ newCart: cartA }, { newCart: cartB }) => {
    if (cartA && !cartB) {
      return 1
    }
    if (!cartA && cartB) {
      return -1
    }
    if (cartA && cartB) {
      return (
        new Date(cartA.updatedTime) > new Date(cartB.updatedTime)
          ? 1
          : -1
      )
    }
    return 0
  })[0]
)

const fetchModules = async () => {
  const [
    { getCart },
    { rawCartDeleteItem },
    { rawCartUpdateQuantity },
  ] = await allPromisesWithRetries(() => [
    import('@/services/Cart/getCart'),
    import('@/services/Cart/rawCartDeleteItem'),
    import('@/services/Cart/rawCartUpdateQuantity'),
  ])
  return {
    getCart,
    rawCartDeleteItem,
    rawCartUpdateQuantity,
  }
}
type ExtendWarrantyNormalizerModules = Awaited<ReturnType<typeof fetchModules>>

const idAndQuantityToOperationPromise = ({
  rawCartUpdateQuantity,
  rawCartDeleteItem,
}: ExtendWarrantyNormalizerModules) => (cartId: string) => (
  ([itemId, quantity]: IDAndQuantitTuple) => (
    quantity > 0
      ? rawCartUpdateQuantity({ cartId, itemId, quantity })
      : rawCartDeleteItem({ cartId, itemId })
  )
)

const normalizeWarrantiesQTY = (
  modules: ExtendWarrantyNormalizerModules,
) => (cartId: string) => (
  idsToAlsoApllyOperator: readonly IDAndQuantitTuple[],
) => (
  Promise.all(
    idsToAlsoApllyOperator.map(
      idAndQuantityToOperationPromise(modules)(cartId),
    ),
  )
)

const withExtendWarrantyNormalization = <T>(
  operator: (...args: T[]) => CartOperatorResponse,
) => async (...args: T[]) => {
    const modules = await fetchModules()
    try {
      const cartOperationResult = await operator(...args)
      const { newCart, success } = cartOperationResult
      if (!success || !newCart) {
        return cartOperationResult
      }
      const extendWarrantyItems = newCart.lineItems.filter(checkIsExtendItem)
      const idsToAlsoApllyOperator = (
        extendWarrantyItems.reduce<IDAndQuantitTuple[]>((acc, item) => {
          const expectedquantity = (
            sumQuantitiesWithSku(
              getExtendingSku(item.sku),
            )(newCart.lineItems)
          )
          if (expectedquantity !== item.quantity) {
            return [
              ...acc,
              [item.id, expectedquantity],
            ]
          }
          return acc
        }, [])
      )
      if (!idsToAlsoApllyOperator.length) {
        return cartOperationResult
      }
      return getLastPromiseToResolve(
        await normalizeWarrantiesQTY(modules)(newCart.id)(idsToAlsoApllyOperator),
      )
    } catch (err) {
      error('Failed to Normalize cart with Extend')
    }
    return { success: false, newCart: await modules.getCart() }
  }

export default withExtendWarrantyNormalization
